import * as React from "react";

import { cn } from "@/shared/utils/utils";
import type { DynamicFieldTableSkeletonProps } from "@/shared/type/skeleton.type";
import { Skeleton } from "./skeleton";

export const DynamicFieldTableSkeleton = ({
  className,
  columns,
  rowCount = 5,
  ...props
}: DynamicFieldTableSkeletonProps): React.ReactElement => {
  const safeRowCount = Math.max(1, rowCount);
  const safeColumns = columns.length > 0 ? columns : [{}];
  const rowIndexes = [...Array(safeRowCount).keys()];

  return (
    <div
      aria-busy="true"
      aria-label="Loading editable table"
      data-slot="dynamic-field-table-skeleton"
      className={cn("table-wrapper overflow-hidden", className)}
      {...props}
    >
      <span className="sr-only" role="status">
        Loading editable table data
      </span>

      <div className="overflow-x-auto w-full max-w-full">
        <table className="ai-custom-table w-full border-collapse">
          <thead className="bg-border [&>tr]:h-11.5">
            <tr>
              {safeColumns.map((_, columnIndex) => (
                <th key={columnIndex} className="px-3 py-2">
                  <Skeleton
                    className={cn(
                      "h-4 w-24 max-w-full",
                      columns?.[columnIndex]?.headerClassName,
                    )}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowIndexes.map((rowIndex) => (
              <tr
                key={rowIndex}
                data-slot="data-table-skeleton-row"
                className="h-11 border-b border-border"
              >
                {safeColumns.map((_, columnIndex) => (
                  <td key={columnIndex} className="px-3 py-2">
                    <Skeleton
                      className={cn(
                        "h-4 w-full max-w-36",
                        columnIndex === 0 && "max-w-48",
                        columns?.[columnIndex]?.cellClassName,
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
